import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperclipIcon, PlusIcon } from "lucide-react";

export default function ClienteForm({ onCreated, compact = false, defaultTipo = "destino" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({ nome: "", cnpj: "", cidade: "", estado: "" });
  const [contractFile, setContractFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.nome || !form.cnpj) return toast.error("Nome e CNPJ obrigatórios");
    setBusy(true);
    try {
      let payload = { ...form, tipo: defaultTipo };
      if (isAdmin && contractFile) {
        payload.contract_filename = contractFile.name;
        payload.contract_data_url = await fileToDataUrl(contractFile);
      }
      const { data } = await api.post("/admin/clientes", payload);
      toast.success("Cliente cadastrado");
      setForm({ nome: "", cnpj: "", cidade: "", estado: "" });
      setContractFile(null);
      onCreated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`grid grid-cols-1 ${compact ? "md:grid-cols-2" : "md:grid-cols-5"} gap-3`}>
      <div className={compact ? "md:col-span-2" : "md:col-span-2"}>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome / Razão Social</Label>
        <Input data-testid="cli-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">CNPJ</Label>
        <Input data-testid="cli-cnpj" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" placeholder="00.000.000/0001-00" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Cidade</Label>
        <Input data-testid="cli-cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">UF</Label>
        <Input data-testid="cli-estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value.toUpperCase() })} className="mt-1 rounded-none bg-[#0A0A0C] h-10 font-mono" maxLength={2} placeholder="SP" />
      </div>

      {/* Contract upload — ADMIN ONLY */}
      {isAdmin && (
        <div className={compact ? "md:col-span-2" : "md:col-span-5"}>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <PaperclipIcon className="w-3 h-3" /> Anexar Contrato (PDF/Imagem) · ADM only
          </Label>
          <input
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => setContractFile(e.target.files?.[0] || null)}
            data-testid="cli-contract"
            className="mt-1 w-full rounded-none bg-[#0A0A0C] border border-border h-10 text-xs file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#FFD60A] file:text-black file:font-bold file:uppercase file:text-[10px] file:cursor-pointer"
          />
          {contractFile && <div className="text-[11px] text-[#34C759] mt-1 font-mono">📎 {contractFile.name}</div>}
        </div>
      )}

      <div className={`${compact ? "md:col-span-2" : "md:col-span-5"} flex justify-end`}>
        <Button onClick={submit} disabled={busy} data-testid="cli-submit" className="h-10 rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs px-6">
          <PlusIcon className="w-4 h-4 mr-1" /> {busy ? "Cadastrando..." : "Cadastrar cliente"}
        </Button>
      </div>
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
